import i18next from 'i18next';
import type modules from 'shared/services/TranslatorNext/i18n/en/modules.json';
import type common from 'shared/services/TranslatorNext/i18n/en/common.json';
import type marketingTerms from 'shared/services/TranslatorNext/i18n/en/marketingTerms.json';
import type login from 'shared/services/TranslatorNext/i18n/en/login.json';
import { Upper, Lower, CapFirst, TitleCase, StripNoTranslate, ReportMissingKey, TranslationToKey } from 'shared/services/TranslatorNext/PostProcessors';
import type { LanguageCode } from 'shared/constants/LanguageOptions';
import { LANGUAGE_OPTIONS } from 'shared/constants/LanguageOptions';

export enum Screen {
    LOGIN = 'login',
    MAIN_APP = 'mainApp'
}

let initPromise: Promise<void> | null = null;

// Helper function to read a cookie by name using plain JS
function getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c!.charAt(0) === ' ') {
            c = c!.substring(1, c!.length);
        }
        if (c!.indexOf(nameEQ) === 0) {
            return c!.substring(nameEQ.length, c!.length);
        }
    }
    return null;
}

// Helper function to set the language cookie using the same behaviour as CookieUtils.set
function setLanguageCookie(value: string) {
	const path = '/';
	const otherOptions: { samesite: string | null; secure: boolean } = {
		samesite: 'none',
		secure: true
	};

	let { samesite } = otherOptions;

	// For MacOS and iOS with versions 10-12, samesite none must not be set.
	// See https://www.chromium.org/updates/same-site/incompatible-clients
	if (samesite === 'none' &&
		/^.*iPhone; CPU iPhone OS 1[0-2].*$|^.*iPad; CPU OS 1[0-2].*$|^.*iPod touch; CPU iPhone OS 1[0-2].*$|^.*Macintosh; Intel Mac OS X.*Version\/1[0-2].*Safari.*$/.test(navigator.userAgent)) {
		samesite = null;
	}

	document.cookie =
		'lang' +
		'=' +
		value +
		(path ? '; path=' + path : '') +
		(samesite ? ';samesite=' + samesite : '') +
		(otherOptions.secure ? ';secure' : '');
}

// Helper to fetch URL parameter from either standard query (?key=) or hash fragment (#/path?key=)
export function getUrlParam(paramName: string): string | null {
    try {
        const fromSearch = new URLSearchParams(window.location.search).get(paramName);
        if (fromSearch) {
            return fromSearch;
        }
        const hash = window.location.hash || '';
        const qIndex = hash.indexOf('?');
        if (qIndex !== -1) {
            const fragmentQuery = hash.substring(qIndex + 1);
            const fromHash = new URLSearchParams(fragmentQuery).get(paramName);
            if (fromHash) {
                return fromHash;
            }
        }
        return null;
    } catch (_e) {
        return null;
    }
}

export function setUrlParam(url: string, paramName: string, paramValue: string): string {
    try {
        if (!url) {
            return url;
        }
        const hashIndex = url.indexOf('#');
        if (hashIndex !== -1) {
            const base = url.slice(0, hashIndex + 1);
            const fragment = url.slice(hashIndex + 1);
            const updatedFragment = setUrlParam(fragment, paramName, paramValue);
            return base + updatedFragment;
        }
        const paramPattern = new RegExp(`([?&])${paramName}=([^&]*)`);
        if (paramPattern.test(url)) {
            return url.replace(paramPattern, (_match, sep) => `${sep}${paramName}=${paramValue}`);
        }
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${paramName}=${paramValue}`;
    } catch (_e) {
        return url;
    }
}

export function appendLangFromCookie(url: string): string {
	try {
		if (!url) {
			return url;
		}
		const cookieValue = getCookie('lang');
		if (!cookieValue) {
			return url;
		}
		const encodedLang = encodeURIComponent(cookieValue);
		return setUrlParam(url, 'lang', encodedLang);
	} catch (_e) {
		return url;
	}
}

function nsFor(screen: Screen) {
    if (screen === Screen.LOGIN) {
        return [
            'login',
            'marketingTerms',
            'common'
        ];
    }
    return [
        'common',
        'modules',
        'marketingTerms'
    ];
}

type NamespaceLoadResult = {
    currentLangTranslations: Record<string, any>[],
    enTranslations: Record<string, any>[]
}

async function loadNamespaces(initialLang: string, namespaces: string[]): Promise<NamespaceLoadResult> {
    if (initialLang === 'en') {
        const enTranslations = await Promise.all(namespaces.map((namespace) => import(`../../services/TranslatorNext/i18n/en/${namespace}.json`).then((m) => m.default)));

        return {
            currentLangTranslations: enTranslations,
            enTranslations
        };
    }

    const [currentLangTranslations, enTranslations] = await Promise.all([
        Promise.all(namespaces.map((namespace) => import(`../../services/TranslatorNext/i18n/${initialLang}/${namespace}.json`).then((m) => m.default))),
        Promise.all(namespaces.map((namespace) => import(`../../services/TranslatorNext/i18n/en/${namespace}.json`).then((m) => m.default)))
    ]);

    return {
        currentLangTranslations,
        enTranslations
    };
}

async function loadMissingNamespaces(initialLang: string, namespaces: string[]) {
    const missing = namespaces.filter(n => !i18next.hasResourceBundle(initialLang, n));
    if (missing.length) {
        const { currentLangTranslations, enTranslations } = await loadNamespaces(initialLang, missing);
        missing.forEach((namespace) => {
            i18next.addResourceBundle(initialLang, namespace, currentLangTranslations[missing.indexOf(namespace)]);
            i18next.addResourceBundle('en', namespace, enTranslations[missing.indexOf(namespace)]);
        });
    }
}

// Initialize i18next with proper cache busting
export async function initializeI18next(screen: Screen): Promise<void> {
    const namespaces = nsFor(screen);

    let data;
    try {
        data = await window.firstLoginCallPromise;
    } catch (_) {
        // do nothing
    }
    const sharedView = !!getUrlParam('token');
    const urlLang = getUrlParam('lang');
    let initialLang = (sharedView && urlLang) ? urlLang : data?.locale || getCookie('lang') || 'en';

    const possibleLanguages = LANGUAGE_OPTIONS.map(language => language.value);
    if (!possibleLanguages.includes(initialLang as LanguageCode)) {
        initialLang = 'en';
    }

    if (typeof window.i18nextInstance !== 'undefined' && window.i18nextInstance.isInitialized) {
        // Already initialized: only ensure the needed namespaces are loaded
        await loadMissingNamespaces(initialLang, namespaces);
        return;
    }

    if (initPromise) {
        // Init in progress: wait, then ensure namespaces
        await initPromise;
        await loadMissingNamespaces(initialLang, namespaces);
        return;
    }

    initPromise = (async () => {
        i18next.on('failedLoading', () => {
            window.location.reload();
        });

        await i18next
            .use(Upper)
            .use(Lower)
            .use(CapFirst)
            .use(TitleCase)
            .use(StripNoTranslate)
            .use(TranslationToKey)
            .use(ReportMissingKey)
            .init({
                lng: initialLang,
                fallbackLng: 'en',
                interpolation: {
                    escapeValue: false
                },
                resources: {},
                defaultNS: false,
                postProcess: ['stripNoTranslate', 'reportMissingKey', 'translationToKey']
            });

        const namespaces = nsFor(screen);
        const { currentLangTranslations, enTranslations } = await loadNamespaces(initialLang, namespaces);
        namespaces.forEach((namespace, index) => {
            i18next.addResourceBundle(initialLang, namespace, currentLangTranslations[index]);
            i18next.addResourceBundle('en', namespace, enTranslations[index]);
        });

        window.i18nextInstance = i18next;
    })();

    await initPromise;
}

/**
 * Translates a key using i18next. Handles interpolation.
 * @param key The translation key (e.g., 'userProfile.title').
 * @param options Optional parameters for interpolation (e.g., { userName: 'Bob' }).
 * @returns The translated string, or the key if not found/ready.
 */
export function t(key: AllTranslationKeys, options?: TranslationVariables) {
    if (!i18next.isInitialized) {
        return key;
    }
    return i18next.t(key, options);
}

/**
 * Changes the application language.
 * Does not return a promise; relies on i18next events or app reloads.
 * @param lng The language code (e.g., 'en', 'ja').
 */
export function changeLanguage(lng: LanguageCode) {
    const sharedView = !!getUrlParam('token');
    if (sharedView) {
        return;
    }
    const language = lng || 'en';
    setLanguageCookie(language);
	document.documentElement.setAttribute('lang', language);
    if (i18next.isInitialized) {
        i18next.changeLanguage(language);
    } else {
        // Optionally handle case where it's called before init, maybe log a warning
    }
}


/**
 * Gets the currently detected or set language.
 * @returns The current language code (e.g., 'en', 'ja') or fallback 'en'.
 */
export function getCurrentLanguage() {
    if (!i18next.isInitialized) {
        return 'en';
    }
    return i18next.language as LanguageCode;
}

/**
 * Provides direct access to the i18next instance if needed for advanced use cases.
 * Returns null if i18next is not initialized.
 */
export function getInstance() {
    // You might want to return window.i18nextInstance here too, or keep it separate
    return window.i18nextInstance || null;
}


type StripAfterUnderscore<T extends string> =
  T extends `${infer Head}_${string}`
    ? Head extends `${string}_${string}`
      ? StripAfterUnderscore<Head>
      : Head
    : T;
type DotPrefix<T extends string, U extends string> = `${T}.${U}`;
type DotNestedKeys<T> = (
  T extends object
    ? {
        [K in keyof T & string]:
          T[K] extends object
            ? DotPrefix<K, DotNestedKeys<T[K]>>
            : StripAfterUnderscore<K>
      }[keyof T & string]
    : ''
);

type AllModulesKeys = `modules:${DotNestedKeys<typeof modules>}`;
type AllCommonKeys = `common:${DotNestedKeys<typeof common>}`;
type AllMarketingTermsKeys = `marketingTerms:${DotNestedKeys<typeof marketingTerms>}`;
type AllLoginKeys = `login:${DotNestedKeys<typeof login>}`;
export type AllTranslationKeys = AllModulesKeys | AllCommonKeys | AllMarketingTermsKeys | AllLoginKeys;
export type TranslationVariables = Record<string, string | number | boolean | string[] | number[]>;




